import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Store, MapPin, Phone, Mail, Sparkles, Plus, Upload, X, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const ContributeVendorPage = () => {
  const navigate = useNavigate();
  const [vendorImage, setVendorImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    vendorName: '',
    category: '',
    department: '',
    location: '',
    state: '',
    phone: '',
    email: '',
    website: '',
    services: '',
    description: '',
    yearsInBusiness: ''
  });

  const departments = [
    'Art Department',
    'Camera Department',
    'Sound Department',
    'Makeup & Hair',
    'SFX / VFX',
    'Production',
    'Assistant Directors',
    'Lighting Department',
    'Post Production',
    'Script Department',
    'Costume Department',
    'General Equipment'
  ];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }
    
    setVendorImage(file);
  };

  const uploadVendorImage = async () => {
    if (!vendorImage) return null;
    
    setUploadingImage(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', vendorImage);
      
      const response = await fetch(`${backendUrl}/api/uploads/vendor-image`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload image');
      }
      
      const data = await response.json();
      return data.file_url;
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload vendor image');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Upload vendor image first if selected
      let imageUrl = null;
      if (vendorImage) {
        toast.info('Uploading vendor image...');
        imageUrl = await uploadVendorImage();
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare vendor data for API
      const vendorData = {
        vendor_name: formData.vendorName,
        category: formData.category,
        department: formData.department,
        city: formData.location,
        state: formData.state,
        mobile_no: formData.phone,
        address: formData.email, // Using email as contact
        location_pin: '', // Optional
        description: formData.description,
        services: formData.services.split(',').map(s => s.trim()).filter(s => s),
        image: imageUrl // Add uploaded image URL
      };
      
      const response = await fetch(`${backendUrl}/api/vendors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(vendorData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit vendor');
      }
      
      toast.success('✅ Vendor added successfully! It will appear in listings immediately.');
      
      // Reset form
      setFormData({
        vendorName: '',
        category: '',
        department: '',
        location: '',
        state: '',
        phone: '',
        email: '',
        website: '',
        services: '',
        description: '',
        yearsInBusiness: ''
      });
      
      // Redirect to vendor listings after 2 seconds
      setTimeout(() => {
        navigate('/departments');
      }, 2000);
      
    } catch (error) {
      console.error('Vendor submission error:', error);
      toast.error('Failed to submit vendor. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="bg-accent/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-10 h-10 text-accent" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Contribute Vendor Data
            </h1>
            <p className="text-lg text-muted-foreground">
              Help build India&apos;s largest film industry vendor database. List vendors you know for FREE - no account needed!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <Card className="bg-card border-border shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">
                  Add a Vendor
                </CardTitle>
                <p className="text-muted-foreground">
                  Share details of vendors, shops, or service providers you&apos;ve worked with
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Store className="w-5 h-5 text-accent" />
                      Vendor Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium mb-2 block">Vendor/Shop Name *</label>
                        <Input
                          required
                          value={formData.vendorName}
                          onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                          placeholder="e.g., Creative Props & Sets"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Category *</label>
                        <Input
                          required
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          placeholder="e.g., Props Rental, Camera Equipment"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Department *</label>
                        <Select value={formData.department} onValueChange={(val) => setFormData({ ...formData, department: val })}>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent>
                            {departments.map(dept => (
                              <SelectItem key={dept} value={dept.toLowerCase().replace(/\s+/g, '-')}>{dept}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Vendor Image Upload */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Image className="w-5 h-5 text-accent" />
                      Vendor Image (Optional)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Upload a logo or photo of your business (JPG, PNG, GIF - Max 10MB)
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                          id="vendor-image-upload"
                        />
                        <label
                          htmlFor="vendor-image-upload"
                          className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-accent/5 transition-colors"
                        >
                          <Upload className="w-4 h-4" />
                          <span className="text-sm">{vendorImage ? 'Change Image' : 'Choose Image'}</span>
                        </label>
                        {vendorImage && (
                          <span className="text-sm text-accent">✓ {vendorImage.name}</span>
                        )}
                      </div>
                      
                      {vendorImage && (
                        <div className="relative inline-block">
                          <img
                            src={URL.createObjectURL(vendorImage)}
                            alt="Vendor preview"
                            className="w-48 h-48 object-cover rounded-lg border border-border"
                          />
                          <button
                            type="button"
                            onClick={() => setVendorImage(null)}
                            className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 hover:bg-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-accent" />
                      Location
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">City *</label>
                        <Input
                          required
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="e.g., Mumbai"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">State *</label>
                        <Input
                          required
                          value={formData.state}
                          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          placeholder="e.g., Maharashtra"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Phone className="w-5 h-5 text-accent" />
                      Contact Details
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Phone Number</label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Email Address</label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="contact@vendor.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Website</label>
                        <Input
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder="https://www.vendor.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Years in Business</label>
                        <Input
                          type="number"
                          value={formData.yearsInBusiness}
                          onChange={(e) => setFormData({ ...formData, yearsInBusiness: e.target.value })}
                          placeholder="e.g., 10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Services & Description */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Services & Details</h3>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Services Offered</label>
                      <Input
                        value={formData.services}
                        onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                        placeholder="Comma separated: Props Rental, Custom Props, Set Pieces"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Description</label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of the vendor and their services..."
                        rows={4}
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white py-6 text-lg">
                      <Plus className="w-5 h-5 mr-2" />
                      Submit Vendor for Review
                    </Button>
                    <p className="text-xs text-center text-muted-foreground mt-4">
                      Your submission will be reviewed and published within 24 hours. Thank you for contributing!
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-card mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2024 Shoot Base. Built with passion for the film industry.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContributeVendorPage;